import { useEffect, useState } from 'react';

/**
 * The dashboard shell writes the active programme into the address with
 * history.replaceState, which the router does not see. A WHS link that omits it
 * lets the next server refresh (after a save) lose the programme and land on the
 * programme chooser. Carry it explicitly on every WHS incident navigation.
 */
export function whsHref(path: string, program: string | null | undefined): string {
  if (!program) return path;
  const [base, hash] = path.split('#');
  const joiner = (base ?? path).includes('?') ? '&' : '?';
  return `${base}${joiner}program=${encodeURIComponent(program)}${hash ? `#${hash}` : ''}`;
}

/**
 * The programme as the browser address shows it. Read from the address itself, not
 * the router, because the shell writes it there without the router knowing.
 */
export function useWhsProgram(): string | null {
  const [program, setProgram] = useState<string | null>(null);
  useEffect(() => {
    const read = () => setProgram(new URLSearchParams(window.location.search).get('program'));
    read();
    // The shell may add the programme just after this page mounts.
    const timer = window.setTimeout(read, 0);
    window.addEventListener('popstate', read);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('popstate', read);
    };
  }, []);
  return program;
}

/** For click handlers: the programme in the address at the moment of the click. */
export function currentWhsProgram(): string | null {
  return typeof window === 'undefined'
    ? null
    : new URLSearchParams(window.location.search).get('program');
}
